import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowLeft, Package, User as UserIcon, Truck, CreditCard, ShieldCheck, Check, XCircle, FileText, Download, X } from 'lucide-react';
import { fetchOrderById } from '../../../features/orderSlice';
import { formatMoney as fmtMoney, normalizeCurrencyRates } from '../../../util/money';
import API from '../../../util/API';
import { toast } from 'react-toastify';
import countryNames from '../../../util/countryNames';
import { resolveOrderUsdValue } from '../../../util/orderCurrency';
import {
  downloadOrderInvoice,
  getOrderInvoicePdfBlob,
} from '../../../util/orderInvoiceAccess';

const cx = (...arr) => arr.filter(Boolean).join(' ');

const Card = ({ children, className }) => (
  <div className={cx('bg-white border border-slate-200 rounded-2xl', className)}>{children}</div>
);

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
};

const normalizeSpaces = (value) => String(value || '').trim().replace(/\s+/g, ' ');

const COUNTRY_NAME_TO_ISO2 = Object.entries(countryNames || {}).reduce((acc, [code, name]) => {
  const key = normalizeSpaces(name).toLowerCase();
  if (key) acc[key] = String(code || '').toUpperCase();
  return acc;
}, {});

const normalizeCountryToISO2 = (...candidates) => {
  for (const c of candidates) {
    const raw = normalizeSpaces(c);
    if (!raw) continue;

    if (/^[A-Za-z]{2}$/.test(raw)) return raw.toUpperCase();

    const parenCode = raw.match(/\(([A-Za-z]{2})\)/);
    if (parenCode?.[1]) return String(parenCode[1]).toUpperCase();

    const byName = COUNTRY_NAME_TO_ISO2[raw.toLowerCase()];
    if (byName) return byName;
  }
  return '';
};

export default function OrderDetailsPage() {
  const { id } = useParams();
  const dispatch = useDispatch();

  const [aliOrderStatus, setAliOrderStatus] = useState('idle'); // idle | loading | succeeded | failed
  const [aliOrderError, setAliOrderError] = useState(null);
  const [approveStatus, setApproveStatus] = useState('idle'); // idle | loading | succeeded | failed
  const [approveError, setApproveError] = useState(null);
  const [retryForwardStatus, setRetryForwardStatus] = useState('idle');
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelStatus, setCancelStatus] = useState('idle'); // idle | loading | failed
  const [cancelError, setCancelError] = useState(null);
  const [trackingMap, setTrackingMap] = useState(null);
  const [trackingRefreshStatus, setTrackingRefreshStatus] = useState('idle');
  const [trackingItemOpenId, setTrackingItemOpenId] = useState('');
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [invoicePreviewUrl, setInvoicePreviewUrl] = useState('');
  const [invoiceStatus, setInvoiceStatus] = useState('idle');
  const [invoiceDownloadStatus, setInvoiceDownloadStatus] = useState('idle');
  const [invoiceError, setInvoiceError] = useState(null);

  const { order, status, error } = useSelector((state) => state.order || {});

  const currencyPayload = useSelector((state) => state.currency?.currency_list);
  const rates = useMemo(() => normalizeCurrencyRates(currencyPayload), [currencyPayload]);
  const money = (amount, from = 'USD') => fmtMoney(amount, { from, to: from });

  useEffect(() => {
    if (!id) return;
    dispatch(fetchOrderById(id));
  }, [dispatch, id]);

  const loadedOrder = order && String(order?._id || order?.id || '') === String(id) ? order : order;

  useEffect(() => {
    const fetchTrackingMap = async () => {
      const orderMongoId = loadedOrder?._id || loadedOrder?.id || '';
      const orderId = loadedOrder?.orderId || '';
      const outOrderId = String(loadedOrder?.externalOutOrderId || loadedOrder?.orderId || '').trim();

      if (!orderMongoId && !orderId && !outOrderId) {
        setTrackingMap(null);
        return;
      }

      try {
        const res = await API.get('/aliexpress-tracking/map', {
          params: {
            orderMongoId: orderMongoId || undefined,
            orderId: orderId || undefined,
            out_order_id: outOrderId || undefined
          }
        });
        setTrackingMap(res?.data?.data || null);
      } catch (e) {
        setTrackingMap(null);
      }
    };

    if (loadedOrder?._id || loadedOrder?.id || loadedOrder?.orderId || loadedOrder?.externalOutOrderId) {
      fetchTrackingMap();
    }
  }, [loadedOrder?._id, loadedOrder?.id, loadedOrder?.orderId, loadedOrder?.externalOutOrderId]);

  useEffect(() => {
    if (!trackingItemOpenId) return undefined;
    const handlePointerDown = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-tracking-popover="true"]')) return;
      setTrackingItemOpenId('');
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setTrackingItemOpenId('');
    };
    document.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [trackingItemOpenId]);

  const items = useMemo(() => (Array.isArray(loadedOrder?.items) ? loadedOrder.items : []), [loadedOrder]);
  const currency =
    loadedOrder?.paidCurrency ||
    loadedOrder?.paymentResult?.currency ||
    loadedOrder?.currency ||
    items?.[0]?.currency ||
    'USD';
  const usdValue = resolveOrderUsdValue(loadedOrder, rates);
  const usdTotal = Number.isFinite(usdValue.amount)
    ? money(usdValue.amount, 'USD')
    : 'Unavailable: historical rate missing';
  const usdTotalLabel =
    usdValue.source === 'current_rate_estimate'
      ? 'USD estimate'
      : usdValue.source === 'manual_correction'
        ? 'USD corrected value'
      : 'USD equivalent';
  const user = loadedOrder?.user && typeof loadedOrder.user === 'object' ? loadedOrder.user : null;
  const userJoinedDate = user?.createdAt ? new Date(user.createdAt) : null;
  const userJoinedDateText = userJoinedDate && !Number.isNaN(userJoinedDate.getTime())
    ? userJoinedDate.toLocaleDateString()
    : '—';
  const totalSpendRaw = Number.isFinite(Number(user?.totalSpend))
    ? Number(user.totalSpend)
    : (Number.isFinite(Number(user?.totalSpent)) ? Number(user.totalSpent) : Number(loadedOrder?.totalPrice || 0));
  const isApproved = !!loadedOrder?.isApproved;
  const isPaid = !!loadedOrder?.isPaid;
  const isCancelled = String(loadedOrder?.order_status || '') === 'cancelled' || Boolean(loadedOrder?.cancelledAt);
  const cancelledByAdmin = loadedOrder?.cancelledBy && typeof loadedOrder.cancelledBy === 'object' ? loadedOrder.cancelledBy : null;
  const cancelledByText = cancelledByAdmin ? (cancelledByAdmin.email || cancelledByAdmin.username || '') : '';
  const isAliExpressOrdered = !!(
    loadedOrder?.externalOutOrderId ||
    (Array.isArray(loadedOrder?.externalOrderIds) && loadedOrder.externalOrderIds.length) ||
    loadedOrder?.externalStatus ||
    aliOrderStatus === 'succeeded'
  );
  const hasPlacedAliOrder = isAliExpressOrdered || (Array.isArray(trackingMap?.order_ids) && trackingMap.order_ids.length > 0);
  const logesticCost = money(Number(loadedOrder?.shippingPrice || 0), currency);

  const userName =
    loadedOrder?.shippingAddress?.fullName ||
    loadedOrder?.billingAddress?.fullName ||
    user?.name ||
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    user?.username ||
    '—';

  const userEmail =
    loadedOrder?.paymentResult?.email_address ||
    user?.email ||
    '—';

  const userPhone =
    loadedOrder?.shippingAddress?.phone ||
    loadedOrder?.billingAddress?.phone ||
    user?.phone ||
    '—';
  const phoneCountry =
    loadedOrder?.shippingAddress?.countryCode ||
    loadedOrder?.billingAddress?.countryCode ||
    loadedOrder?.shippingAddress?.country ||
    loadedOrder?.billingAddress?.country ||
    '';
  const phoneDisplay = phoneCountry ? `${phoneCountry} ${userPhone}` : userPhone;
  const normalizeTrackingValue = (value) => String(value ?? '').trim().toLowerCase();
  const aliTrackingOrderIds = useMemo(() => {
    const direct = Array.isArray(trackingMap?.order_ids) ? trackingMap.order_ids : [];
    if (direct.length) return direct;

    const mapped = Array.isArray(trackingMap?.item_mappings)
      ? trackingMap.item_mappings.map((item) => item?.order_id).filter(Boolean)
      : [];

    const fromItems = Array.isArray(items)
      ? items.map((item) => item?.ali_order_id).filter(Boolean)
      : [];

    const unique = Array.from(new Set([...mapped, ...fromItems].map((id) => String(id))));
    return unique;
  }, [items, trackingMap]);
  const trackingRecords = useMemo(
    () => (Array.isArray(trackingMap?.tracking_records) ? trackingMap.tracking_records : []),
    [trackingMap]
  );
  const hasAliExpressDelivered = useMemo(() => {
    if (!trackingRecords.length) return false;
    return trackingRecords.some((record) => {
      const events = Array.isArray(record?.tracking_events) ? record.tracking_events : [];
      return events.some((event) => normalizeTrackingValue(event?.tracking_name) === 'delivered');
    });
  }, [trackingRecords]);
  const hasAliExpressLogistics = useMemo(() => {
    if (!trackingRecords.length) return false;
    return trackingRecords.some((record) => (
      Boolean(record?.carrier_name)
      || Boolean(record?.eta_time_stamps)
      || (Array.isArray(record?.tracking_events) && record.tracking_events.length > 0)
    ));
  }, [trackingRecords]);
  const hasTrackingId = aliTrackingOrderIds.length > 0;
  const approavedDisplay = isApproved || hasTrackingId || hasAliExpressLogistics || hasAliExpressDelivered || loadedOrder?.isDelivered;
  const aliExpressHasProgress = isAliExpressOrdered || hasTrackingId || approavedDisplay;
  const aliExpressIsGreen = hasAliExpressLogistics || hasAliExpressDelivered || loadedOrder?.isDelivered;
  const trackingRecordsById = useMemo(() => {
    const map = new Map();
    trackingRecords.forEach((record) => {
      const key = String(record?.order_id || '').trim();
      if (key) map.set(key, record);
    });
    return map;
  }, [trackingRecords]);

  const headerId = String(loadedOrder?.orderId || loadedOrder?._id || loadedOrder?.id || id || '');
  const pdfOrderId = String(loadedOrder?._id || loadedOrder?.id || id || '').trim();
  const invoiceFileName = `order-${String(headerId || pdfOrderId || 'invoice')
    .replace(/[^a-zA-Z0-9_-]+/g, '-')
    .replace(/-+/g, '-')}.pdf`;

  const loadInvoicePreview = useCallback(async () => {
    if (!pdfOrderId) return;

    setInvoiceStatus('loading');
    setInvoiceError(null);

    try {
      const blob = await getOrderInvoicePdfBlob(pdfOrderId, 'view');
      const nextUrl = URL.createObjectURL(blob);

      setInvoicePreviewUrl((currentUrl) => {
        if (currentUrl?.startsWith('blob:')) URL.revokeObjectURL(currentUrl);
        return nextUrl;
      });
      setInvoiceStatus('succeeded');
    } catch (err) {
      setInvoiceStatus('failed');
      setInvoiceError(err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to load invoice PDF');
    }
  }, [pdfOrderId]);

  const openInvoicePreview = useCallback(() => {
    if (!pdfOrderId) return;
    setInvoiceModalOpen(true);
    if (!invoicePreviewUrl && invoiceStatus !== 'loading') {
      loadInvoicePreview();
    }
  }, [invoicePreviewUrl, invoiceStatus, loadInvoicePreview, pdfOrderId]);

  const downloadInvoice = useCallback(async () => {
    if (!pdfOrderId) return;

    setInvoiceDownloadStatus('loading');
    setInvoiceError(null);

    try {
      await downloadOrderInvoice(pdfOrderId, invoiceFileName);
      setInvoiceDownloadStatus('succeeded');
    } catch (err) {
      setInvoiceDownloadStatus('failed');
      setInvoiceError(err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to download invoice PDF');
    }
  }, [invoiceFileName, pdfOrderId]);

  useEffect(() => {
    if (!invoiceModalOpen) return undefined;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setInvoiceModalOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [invoiceModalOpen]);

  useEffect(() => {
    if (!cancelModalOpen) return undefined;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && cancelStatus !== 'loading') setCancelModalOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [cancelModalOpen, cancelStatus]);

  useEffect(() => {
    return () => {
      if (invoicePreviewUrl?.startsWith('blob:')) URL.revokeObjectURL(invoicePreviewUrl);
    };
  }, [invoicePreviewUrl]);

  useEffect(() => {
    setInvoicePreviewUrl((currentUrl) => {
      if (currentUrl?.startsWith('blob:')) URL.revokeObjectURL(currentUrl);
      return '';
    });
    setInvoiceStatus('idle');
    setInvoiceDownloadStatus('idle');
    setInvoiceError(null);
  }, [pdfOrderId]);

  const orderMongoId = String(loadedOrder?._id || loadedOrder?.id || id || '').trim();

  const aliPlaceOrderRequest = useMemo(() => {
    const shipping = loadedOrder?.shippingAddress && typeof loadedOrder.shippingAddress === 'object'
      ? loadedOrder.shippingAddress
      : {};

    const countryCode = normalizeCountryToISO2(
      shipping.countryCode,
      shipping.country_code,
      shipping.countryISO2,
      shipping.countryIso2,
      shipping.country
    );

    const rawPhone = String(shipping.phone || '').trim();
    const digitsPhone = rawPhone.replace(/[^0-9]/g, '');

    const outOrderId = String(loadedOrder?.orderId || '').trim()
      || String(loadedOrder?.externalOutOrderId || '').trim()
      || `cherrfy_${String(loadedOrder?._id || id || Date.now())}`;

    return {
      currency: 'USD',
      try_to_pay: false,
      out_order_id: outOrderId,
      promotion_code: '',
      promotion_channel_info: '',
      logistics_address: {
        address: String(shipping.address || ''),
        city: String(shipping.city || ''),
        contact_person: String(shipping.fullName || userName || ''),
        country: countryCode,
        mobile_no: digitsPhone,
        phone_country: '+',
        phone_number: digitsPhone,
        province: String(shipping.province || shipping.state || shipping.region || shipping.city || ''),
        zip: String(shipping.postalCode || ''),
      },
      product_items: (Array.isArray(items) ? items : []).map((it) => ({
        product_id: it?.productId ?? '',
        product_count: Number(it?.quantity || 0),
        sku_id: String(it?.sku_id || ''),
        sku_attr: String(it?.sku_attr || ''),
        logistics_service_name: String(it?.logistics_service_name || ''),
        order_memo: String(it?.order_memo || ''),
      })),
    };
  }, [id, items, loadedOrder, userName]);

  const aliMissingFields = useMemo(() => {
    const missing = [];

    const currencyValue = String(aliPlaceOrderRequest?.currency || '').trim();
    if (!currencyValue) missing.push('currency');

    const addr = aliPlaceOrderRequest?.logistics_address && typeof aliPlaceOrderRequest.logistics_address === 'object'
      ? aliPlaceOrderRequest.logistics_address
      : {};

    const requiredAddr = ['address', 'city', 'contact_person', 'country', 'phone_country', 'phone_number', 'province', 'zip'];
    requiredAddr.forEach((k) => {
      if (!String(addr?.[k] ?? '').trim()) missing.push(`logistics_address.${k}`);
    });

    const productItems = Array.isArray(aliPlaceOrderRequest?.product_items) ? aliPlaceOrderRequest.product_items : [];
    if (!productItems.length) missing.push('product_items');

    productItems.forEach((it, idx) => {
      if (it?.product_id === undefined || it?.product_id === null || String(it?.product_id).trim() === '') {
        missing.push(`product_items[${idx}].product_id`);
      }
      const cnt = Number(it?.product_count || 0);
      if (!Number.isFinite(cnt) || cnt <= 0) missing.push(`product_items[${idx}].product_count`);
      if (!String(it?.sku_attr || '').trim()) missing.push(`product_items[${idx}].sku_attr`);
    });

    return missing;
  }, [aliPlaceOrderRequest]);

  const refreshTrackingForOrderIds = async (orderIds) => {
    const ids = Array.isArray(orderIds) ? orderIds : [];
    if (!ids.length) return;

    setTrackingRefreshStatus('loading');
    try {
      await API.post('/aliexpress-tracking/fetch-many', {
        order_ids: ids,
        orderMongoId: loadedOrder?._id || loadedOrder?.id || null,
        orderId: loadedOrder?.orderId || null,
        out_order_id: aliPlaceOrderRequest?.out_order_id || null,
        language: 'en_US'
      });

      const res = await API.get('/aliexpress-tracking/map', {
        params: {
          orderMongoId: loadedOrder?._id || loadedOrder?.id || undefined,
          orderId: loadedOrder?.orderId || undefined,
          out_order_id: aliPlaceOrderRequest?.out_order_id || undefined
        }
      });
      setTrackingMap(res?.data?.data || null);
      setTrackingRefreshStatus('succeeded');
    } catch (err) {
      setTrackingRefreshStatus('failed');
    }
  };

  const handlePlacedResult = (data) => {
    const ids = Array.isArray(data?.order?.externalOrderIds) ? data.order.externalOrderIds : [];
    if (ids.length) {
      refreshTrackingForOrderIds(ids).catch((fetchErr) => {
        console.error('Failed to fetch AliExpress tracking', fetchErr);
      });
    }
  };

  const approveOrder = async () => {
    if (!orderMongoId) return;
    setApproveStatus('loading');
    setApproveError(null);
    setAliOrderError(null);

    try {
      const res = await API.post(`/order/${orderMongoId}/approve`, {});
      const data = res?.data || {};
      setApproveStatus('succeeded');

      if (data.aliPlaced) {
        setAliOrderStatus('succeeded');
        toast.success('Order approved and forwarded to AliExpress.');
      } else {
        setAliOrderStatus('failed');
        setAliOrderError({
          message: data.aliError
            || 'The order was approved, but forwarding it to AliExpress failed. You can retry forwarding.',
        });
        toast.error('Approved, but AliExpress forwarding failed. Use "Forward to AliExpress" to retry.');
      }

      dispatch(fetchOrderById(id));
      if (data.aliPlaced) handlePlacedResult(data);
    } catch (e) {
      const message = e?.response?.data?.message
        || e?.response?.data?.error
        || e?.message
        || 'Approval failed.';
      setApproveStatus('failed');
      setApproveError(message);
      toast.error(message);
      dispatch(fetchOrderById(id));
    }
  };

  const retryForwardingToAliExpress = async () => {
    if (!orderMongoId) return;
    setRetryForwardStatus('loading');
    setAliOrderError(null);

    try {
      const res = await API.post(`/order/${orderMongoId}/place-on-aliexpress`, {});
      const data = res?.data || {};
      if (data.aliPlaced) {
        setAliOrderStatus('succeeded');
        toast.success(data.alreadyPlaced
          ? 'Order is already placed on AliExpress.'
          : 'Order forwarded to AliExpress.');
        dispatch(fetchOrderById(id));
        if (!data.alreadyPlaced) handlePlacedResult(data);
      } else {
        setAliOrderStatus('failed');
        setAliOrderError({
          message: data.aliError || 'AliExpress forwarding failed again. Check the order data and retry.',
        });
        toast.error('AliExpress forwarding failed.');
      }
    } catch (e) {
      setAliOrderStatus('failed');
      setAliOrderError({
        message: e?.response?.data?.message
          || e?.response?.data?.error
          || e?.message
          || 'AliExpress request failed',
      });
      toast.error('AliExpress forwarding failed.');
    } finally {
      setRetryForwardStatus('idle');
    }
  };

  const openCancelModal = () => {
    if (!orderMongoId || isCancelled) return;
    setCancelReason('');
    setCancelError(null);
    setCancelModalOpen(true);
  };

  const confirmCancelOrder = async () => {
    if (!orderMongoId) return;
    const reason = String(cancelReason || '').trim();
    if (!reason) {
      setCancelError('A cancellation reason is required.');
      return;
    }

    setCancelStatus('loading');
    setCancelError(null);
    try {
      const res = await API.post(`/order/${orderMongoId}/cancel`, { reason });
      const data = res?.data || {};
      setCancelModalOpen(false);
      setCancelStatus('idle');
      if (data.wasPaid) {
        toast.warn(data.message || 'Order cancelled. A manual refund is required because this order was paid.');
      } else {
        toast.success(data.message || 'Order cancelled.');
      }
      dispatch(fetchOrderById(id));
    } catch (e) {
      const message = e?.response?.data?.message
        || e?.response?.data?.error
        || e?.message
        || 'Cancel failed.';
      setCancelStatus('failed');
      setCancelError(message);
      toast.error(message);
      dispatch(fetchOrderById(id));
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="h-4 w-[3px] rounded-full bg-primary" aria-hidden="true" />
            <h2 className="text-base md:text-lg font-black tracking-tight text-slate-900">Order Full View</h2>
          </div>
          <p className="mt-1 text-sm text-slate-500">
            Order #{headerId} • {formatDateTime(loadedOrder?.createdAt)}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={openInvoicePreview}
            disabled={!pdfOrderId || invoiceStatus === 'loading'}
            className={cx(
              'inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-black text-slate-700 hover:bg-slate-50',
              (!pdfOrderId || invoiceStatus === 'loading') ? 'opacity-60 cursor-not-allowed' : ''
            )}
          >
            <FileText className="w-4 h-4" />
            {invoiceStatus === 'loading' ? 'Loading PDF...' : 'View PDF'}
          </button>
          <button
            type="button"
            onClick={downloadInvoice}
            disabled={!pdfOrderId || invoiceDownloadStatus === 'loading'}
            className={cx(
              'inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-black text-slate-700 hover:bg-slate-50',
              (!pdfOrderId || invoiceDownloadStatus === 'loading') ? 'opacity-60 cursor-not-allowed' : ''
            )}
          >
            <Download className="w-4 h-4" />
            {invoiceDownloadStatus === 'loading' ? 'Downloading...' : 'Download PDF'}
          </button>
          <button
            type="button"
            onClick={approveOrder}
            disabled={!isPaid || isApproved || isCancelled || approveStatus === 'loading'}
            title={isCancelled ? 'Cancelled orders cannot be approved' : (!isPaid ? 'Order must be paid before approval' : (isApproved ? 'Order already approved' : 'Approve order and forward to AliExpress'))}
            className={cx(
              'inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-black',
              (!isPaid || isApproved || isCancelled || approveStatus === 'loading')
                ? 'border border-emerald-200 bg-emerald-50 text-emerald-700 cursor-not-allowed'
                : 'bg-primary text-white hover:opacity-95 shadow-[0_0_12px_rgba(210,4,45,0.35)]'
            )}
          >
            <ShieldCheck className="w-4 h-4" />
            {approveStatus === 'loading' ? 'Approving…' : (isApproved ? 'Approved' : (isPaid ? 'Approve' : 'Approve (unpaid)'))}
          </button>
          <button
            type="button"
            onClick={retryForwardingToAliExpress}
            disabled={!isPaid || hasPlacedAliOrder || isCancelled || retryForwardStatus === 'loading' || approveStatus === 'loading'}
            title={isCancelled ? 'Cancelled orders cannot be forwarded' : (!isPaid ? 'Order must be paid before forwarding to AliExpress' : (hasPlacedAliOrder ? 'Already forwarded to AliExpress' : 'Forward this order to AliExpress'))}
            className={cx(
              'inline-flex items-center justify-center gap-2 rounded-xl border px-4 py-2 text-sm font-black',
              (!isPaid || hasPlacedAliOrder || isCancelled || retryForwardStatus === 'loading')
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700 cursor-not-allowed'
                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
            )}
          >
            <Package className="w-4 h-4" />
            {retryForwardStatus === 'loading' ? 'Forwarding…' : (hasPlacedAliOrder ? 'Forwarded to AliExpress' : 'Forward to AliExpress')}
          </button>
          <button
            type="button"
            onClick={openCancelModal}
            disabled={isCancelled || cancelStatus === 'loading'}
            title={isCancelled ? 'Order is already cancelled' : 'Cancel this order (reason required)'}
            className={cx(
              'inline-flex items-center justify-center gap-2 rounded-xl border px-4 py-2 text-sm font-black',
              (isCancelled || cancelStatus === 'loading')
                ? 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
                : 'border-rose-200 bg-white text-rose-700 hover:bg-rose-50'
            )}
          >
            <XCircle className="w-4 h-4" />
            {isCancelled ? 'Cancelled' : 'Cancel order'}
          </button>
          <Link
            to="/administrator/orders"
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-black text-slate-700 hover:bg-slate-50"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
        </div>
      </div>

      {aliMissingFields.length > 0 ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-xs text-amber-900">
          Missing required fields for AliExpress order. Please complete order details before approving.
        </div>
      ) : null}

      {approveStatus === 'failed' && approveError ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-xs text-rose-800">
          {String(approveError)}
        </div>
      ) : null}

      {aliOrderStatus === 'failed' && aliOrderError ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-xs text-rose-800 flex flex-wrap items-center justify-between gap-3">
          <span>{String(aliOrderError?.message || aliOrderError?.error || 'AliExpress request failed')}</span>
          {!hasPlacedAliOrder ? (
            <button
              type="button"
              onClick={retryForwardingToAliExpress}
              disabled={retryForwardStatus === 'loading'}
              className={cx(
                'inline-flex items-center justify-center gap-2 rounded-xl border border-rose-300 bg-white px-3 py-1.5 text-xs font-black text-rose-700 hover:bg-rose-50',
                retryForwardStatus === 'loading' ? 'opacity-60 cursor-not-allowed' : ''
              )}
            >
              {retryForwardStatus === 'loading' ? 'Retrying…' : 'Retry forwarding'}
            </button>
          ) : null}
        </div>
      ) : null}

      {isCancelled ? (
        <div className="rounded-2xl border border-slate-300 bg-slate-50 px-5 py-4 text-xs text-slate-800">
          <span className="font-black">Order cancelled</span>
          {loadedOrder?.cancelledAt ? ` on ${formatDateTime(loadedOrder.cancelledAt)}` : ''}
          {cancelledByText ? ` by ${cancelledByText}` : ''}
          {loadedOrder?.cancellationReason ? ` — reason: ${String(loadedOrder.cancellationReason)}` : ''}
          {isPaid ? ' — this order was PAID: a manual refund in the payment provider is required.' : ''}
        </div>
      ) : null}

      {status === 'loading' ? (
        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4 text-sm text-slate-700">
          Loading order…
        </div>
      ) : null}

      {status === 'failed' && error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-800">
          {typeof error === 'string' ? error : (error?.error || error?.message || 'Failed to load order')}
        </div>
      ) : null}

      {invoiceStatus === 'failed' && invoiceError ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-800">
          {String(invoiceError)}
        </div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-5">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-black tracking-tight text-slate-900">Order</h3>
              <span className="text-xs font-semibold text-slate-500 font-mono break-all">{String(loadedOrder?._id || '—')}</span>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-bold text-slate-500">Order ID</p>
                <p className="mt-0.5 font-mono text-sm font-black text-primary">{String(loadedOrder?.orderId || '—')}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-slate-500">Payment method</p>
                <p className="mt-0.5 font-semibold text-slate-900">
                  {String(loadedOrder?.paymentResult?.paymentMethodLabel || loadedOrder?.paymentMethod || '—')}
                </p>
              </div>
              <div>
                <p className="text-xs font-bold text-slate-500">Status</p>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  {!approavedDisplay ? (
                    <span className={cx(
                      'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-black',
                      'border-rose-200 bg-rose-50 text-rose-700'
                    )}>
                      <XCircle className="w-3.5 h-3.5" /> Not Approaved
                    </span>
                  ) : (
                    <span className={cx(
                      'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-black',
                      'border-emerald-200 bg-emerald-50 text-emerald-700'
                    )}>
                      <ShieldCheck className="w-3.5 h-3.5" /> Approaved
                    </span>
                  )}
                  <span className={cx(
                    'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-black',
                    aliExpressIsGreen
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                      : (aliExpressHasProgress
                        ? 'border-amber-200 bg-amber-50 text-amber-700'
                        : 'border-slate-200 bg-slate-50 text-slate-700')
                  )}>
                    <Package className="w-3.5 h-3.5" /> AliExpress
                  </span>
                  <span className={cx(
                    'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-black',
                    hasAliExpressDelivered || loadedOrder?.isDelivered
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                      : (hasAliExpressLogistics
                        ? 'border-amber-200 bg-amber-50 text-amber-700'
                        : 'border-slate-200 bg-slate-50 text-slate-700')
                  )}>
                    <Truck className="w-3.5 h-3.5" /> Logestic
                  </span>
                  <span className={cx(
                    'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-black',
                    hasAliExpressDelivered || loadedOrder?.isDelivered
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                      : 'border-slate-200 bg-slate-50 text-slate-700'
                  )}>
                    <Check className="w-3.5 h-3.5" /> Delivered
                  </span>
                  <span className={cx(
                    'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-black',
                    loadedOrder?.isPaid
                      ? 'bg-primary text-white shadow-[0_0_12px_rgba(210,4,45,0.35)]'
                      : 'bg-slate-200 text-slate-600'
                  )}>
                    <CreditCard className="w-3.5 h-3.5" /> {loadedOrder?.isPaid ? 'PAID' : 'UNPAID'}
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-black uppercase tracking-wide text-slate-500">
                    {String(loadedOrder?.order_status || '—')}
                  </span>
                </div>
                <p className="mt-2 text-xs font-semibold text-slate-600">Cost of Logestic: <span className="font-black text-slate-900">{logesticCost}</span></p>
              </div>
              <div>
                <p className="text-xs font-bold text-slate-500">Items price</p>
                <p className="mt-0.5 font-semibold text-slate-900">{money(Number(loadedOrder?.itemsPrice || 0), currency)}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-slate-500">Total</p>
                <p className="mt-0.5 font-black text-lg text-slate-900">{money(Number(loadedOrder?.totalPrice || 0), currency)}</p>
                <p className="mt-0.5 text-xs font-semibold text-slate-500">{usdTotalLabel}: {usdTotal}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-slate-500">Shipping</p>
                <p className="mt-0.5 font-semibold text-slate-900">{money(Number(loadedOrder?.shippingPrice || 0), currency)}</p>
              </div>
              {Number(loadedOrder?.discountPrice || 0) > 0 ? (
                <div>
                  <p className="text-xs font-bold text-slate-500">Discount</p>
                  <p className="mt-0.5 font-semibold text-emerald-700">
                    - {money(Number(loadedOrder.discountPrice), currency)}
                  </p>
                </div>
              ) : null}
              <div>
                <p className="text-xs font-bold text-slate-500">Tax</p>
                <p className="mt-0.5 font-semibold text-slate-900">{money(Number(loadedOrder?.taxPrice || 0), currency)}</p>
              </div>
            </div>
          </Card>

          <Card className="p-5">
            <h3 className="text-sm font-black tracking-tight text-slate-900 flex items-center gap-2">
              <Package className="w-4 h-4 text-primary" /> Items
            </h3>

            <div className="mt-4 overflow-x-auto">
              <table className="w-full table-fixed">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="w-[44%] px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Product</th>
                    <th className="w-[8%] px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Qty</th>
                    <th className="w-[12%] px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Price</th>
                    <th className="w-[20%] px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Selected SKU</th>
                    <th className="w-[16%] px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Logistics</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-6 text-sm text-slate-500 text-center">No items</td>
                    </tr>
                  ) : (
                    items.map((item, idx) => {
                      const qty = Number(item?.quantity || 0);
                      const price = Number(item?.price || 0);
                      const pid = item?.productId ? String(item.productId) : '';
                      const skuId = item?.sku_id ? String(item.sku_id) : '—';
                      const skuAttr = item?.sku_attr ? String(item.sku_attr) : '—';
                      const logisticName = item?.logistics_service_name ? String(item.logistics_service_name) : '—';
                      const logisticCode = item?.logistics_service_code ? String(item.logistics_service_code) : '';
                      const logisticCostValue = Number(loadedOrder?.shippingPrice || 0);

                      return (
                        <tr key={`${pid || item?.product || idx}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                          <td className="px-4 py-3 text-sm text-slate-900">
                            <div className="flex items-center gap-3">
                              {item?.image ? (
                                <img src={item.image} alt={item?.name || 'Item'} className="h-10 w-10 rounded-xl object-cover border border-slate-200" />
                              ) : (
                                <div className="h-10 w-10 rounded-xl bg-slate-100 border border-slate-200" />
                              )}
                              <div className="min-w-0">
                                <p className="font-semibold text-slate-900 whitespace-normal break-words line-clamp-2">
                                  {item?.name || '—'}
                                </p>
                                <p className="mt-1 text-xs text-slate-500 font-mono whitespace-normal break-words line-clamp-1">
                                  {[
                                    pid ? `PID: ${pid}` : null,
                                    item?.sku_id ? `SKU: ${String(item.sku_id)}` : null,
                                  ].filter(Boolean).join(' • ') || '—'}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-700">{qty}</td>
                          <td className="px-4 py-3 text-sm text-slate-700">{money(price, currency)}</td>
                          <td className="px-4 py-3 text-xs text-slate-700">
                            <div className="font-mono break-all"><span className="text-slate-500">sku_id:</span> {skuId}</div>
                            <div className="mt-1 font-mono break-all"><span className="text-slate-500">sku_attr:</span> {skuAttr}</div>
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-700">
                            <div className="font-semibold text-slate-900">{logisticName}</div>
                            {logisticCode ? <div className="mt-1 font-mono text-[11px] text-slate-500 break-all">{logisticCode}</div> : null}
                            <div className="mt-1 text-[11px] text-slate-600">Cost: <span className="font-normal text-slate-900">{money(logisticCostValue, currency)}</span></div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          <Card className="p-5">
            <h3 className="text-sm font-black tracking-tight text-slate-900 flex items-center gap-2">
              <Truck className="w-4 h-4 text-primary" /> Tracking
            </h3>
            <div className="mt-4 space-y-3 text-sm">
              {items.length ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {items.map((item, idx) => {
                    const productIdValue = item?.productId ?? '';
                    const skuIdValue = item?.sku_id ?? '';
                    const skuAttrValue = item?.sku_attr ?? '';
                    const productId = String(productIdValue || '—');
                    const skuId = String(skuIdValue || '');
                    const normalizedProductId = normalizeTrackingValue(productIdValue);
                    const normalizedSkuId = normalizeTrackingValue(skuIdValue);
                    const normalizedSkuAttr = normalizeTrackingValue(skuAttrValue);
                    const mappedEntry = Array.isArray(trackingMap?.item_mappings)
                      ? trackingMap.item_mappings.find((entry) => (
                        normalizeTrackingValue(entry?.product_id) === normalizedProductId
                        && normalizeTrackingValue(entry?.sku_id) === normalizedSkuId
                        && normalizeTrackingValue(entry?.sku_attr) === normalizedSkuAttr
                      )) || trackingMap.item_mappings.find((entry) => Number(entry?.serial) === idx + 1)
                      : null;
                    const aliOrderId = String(mappedEntry?.order_id || '');
                    const rowId = `${productId}-${skuId || idx}`;
                    const trackingRecord = aliOrderId ? trackingRecordsById.get(aliOrderId) : null;
                    const displayStatus = trackingRecord?.status === 'Tracking unavailable'
                      ? 'Preparing product'
                      : (trackingRecord?.status || '—');
                    const trackingEventsList = Array.isArray(trackingRecord?.tracking_events)
                      ? trackingRecord.tracking_events
                      : [];
                    const sortedTrackingEvents = [...trackingEventsList].sort(
                      (a, b) => (Number(b?.time_stamp || 0) - Number(a?.time_stamp || 0))
                    );

                    return (
                      <div key={rowId} className="relative rounded-2xl border border-slate-200 bg-white p-4">
                        <button
                          type="button"
                          onClick={() => setTrackingItemOpenId(trackingItemOpenId === rowId ? '' : rowId)}
                          className="w-full text-left"
                          data-tracking-popover="true"
                        >
                          <p className="text-xs font-bold text-slate-500">Product ID</p>
                          <p className="mt-1 font-mono text-xs font-semibold text-slate-900 break-all">{productId}</p>
                          {aliOrderId ? (
                            <p className="mt-2 text-xs text-slate-600">Tracking: <span className="font-semibold text-slate-900">{aliOrderId}</span></p>
                          ) : (
                            <p className="mt-2 text-xs text-slate-500">Tracking: —</p>
                          )}
                        </button>

                        {trackingItemOpenId === rowId ? (
                          <div className="absolute left-0 right-0 bottom-full mb-3 z-20 mx-auto max-w-md rounded-xl border border-slate-200 bg-white p-3 shadow-lg" data-tracking-popover="true">
                            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                              <div>
                                <p className="text-[10px] font-semibold text-slate-500">Carrier</p>
                                <p className="mt-0.5 text-xs font-semibold text-slate-900">{trackingRecord?.carrier_name || '—'}</p>
                                <div className="mt-1 flex items-center gap-2">
                                  <span className="text-[10px] font-semibold text-slate-500">Tracking ID</span>
                                  <span className="font-mono text-[10px] font-semibold text-slate-900 break-all">{aliOrderId || '—'}</span>
                                </div>
                                <div className="mt-1 flex items-center gap-2">
                                  <span className="text-[10px] font-semibold text-slate-500">Global Tracking Number</span>
                                  <span className="font-mono text-[10px] font-semibold text-slate-900 break-all">{trackingRecord?.mail_no || '—'}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                                  {displayStatus}
                                </span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (aliOrderId) refreshTrackingForOrderIds([aliOrderId]);
                                  }}
                                  disabled={!aliOrderId || trackingRefreshStatus === 'loading'}
                                  className={cx(
                                    'rounded-lg border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-700 hover:bg-slate-100',
                                    (!aliOrderId || trackingRefreshStatus === 'loading') ? 'opacity-60 cursor-not-allowed' : ''
                                  )}
                                >
                                  {trackingRefreshStatus === 'loading' ? 'Re-Fetching…' : 'Re-Fetch'}
                                </button>
                              </div>
                            </div>

                            {trackingRecord ? (
                              <div className="max-h-[60vh] overflow-y-auto">
                                {sortedTrackingEvents.length ? (
                                  <div className="relative">
                                    <span className="absolute left-2 top-3 bottom-3 w-px bg-slate-200" aria-hidden="true" />
                                    <div className="space-y-3">
                                      {sortedTrackingEvents.map((event, eventIdx) => {
                                        const isLatest = eventIdx === 0;
                                        return (
                                          <div key={`${event?.tracking_name || 'event'}-${eventIdx}`} className="flex gap-3 items-stretch">
                                            <div className="relative flex w-4 items-center justify-center">
                                              <span
                                                className={cx(
                                                  'h-3 w-3 rounded-full border',
                                                  isLatest ? 'bg-emerald-500 border-emerald-500' : 'bg-white border-slate-300'
                                                )}
                                                aria-hidden="true"
                                              />
                                            </div>
                                            <div className="flex-1 rounded-lg border border-slate-200 bg-white p-2">
                                              <div className="flex flex-wrap items-center justify-between gap-2">
                                                <span className={cx(
                                                  isLatest ? 'text-sm font-semibold text-slate-900' : 'text-xs font-semibold text-slate-600'
                                                )}>
                                                  {String(event?.tracking_name || '—')}
                                                </span>
                                                <span className="text-[10px] text-slate-500">{formatDateTime(event?.time_stamp)}</span>
                                              </div>
                                              {event?.tracking_detail_desc ? (
                                                <div className={cx(
                                                  'mt-1 text-[11px]',
                                                  isLatest ? 'text-slate-700' : 'text-slate-500'
                                                )}>
                                                  {String(event.tracking_detail_desc)}
                                                </div>
                                              ) : (
                                                <div className="mt-1 text-[10px] text-slate-400">No description provided.</div>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[10px] text-slate-600">
                                    No tracking events available.
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-[11px] text-slate-600">No tracking data available yet.</div>
                            )}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-sm text-slate-600">No items available.</div>
              )}
            </div>
          </Card>

        </div>

        <div className="space-y-6">
          <Card className="p-5">
            <h3 className="text-sm font-black tracking-tight text-slate-900 flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary" /> Invoice PDF
            </h3>
            <p className="mt-2 text-xs text-slate-500">
              Preview the invoice in this page or download a PDF copy.
            </p>
            <div className="mt-4 grid grid-cols-1 gap-2">
              <button
                type="button"
                onClick={openInvoicePreview}
                disabled={!pdfOrderId || invoiceStatus === 'loading'}
                className={cx(
                  'inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-3 py-2 text-sm font-black text-white hover:bg-primary/90',
                  (!pdfOrderId || invoiceStatus === 'loading') ? 'opacity-60 cursor-not-allowed' : ''
                )}
              >
                <FileText className="w-4 h-4" />
                {invoiceStatus === 'loading' ? 'Loading PDF...' : 'View invoice PDF'}
              </button>
              <button
                type="button"
                onClick={downloadInvoice}
                disabled={!pdfOrderId || invoiceDownloadStatus === 'loading'}
                className={cx(
                  'inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-black text-slate-700 hover:bg-slate-50',
                  (!pdfOrderId || invoiceDownloadStatus === 'loading') ? 'opacity-60 cursor-not-allowed' : ''
                )}
              >
                <Download className="w-4 h-4" />
                {invoiceDownloadStatus === 'loading' ? 'Downloading...' : 'Download invoice'}
              </button>
            </div>
            {invoiceDownloadStatus === 'failed' && invoiceError ? (
              <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
                {String(invoiceError)}
              </div>
            ) : null}
          </Card>

          <Card className="p-5">
            <h3 className="text-sm font-black tracking-tight text-slate-900 flex items-center gap-2">
              <UserIcon className="w-4 h-4 text-primary" /> User
            </h3>

            <div className="mt-4 space-y-3 text-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <p className="text-xs font-bold text-slate-500">Name</p>
                  <p className="mt-0.5 font-semibold text-slate-900">{userName}</p>
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-500">Email</p>
                  <p className="mt-0.5 font-semibold text-slate-900 break-all">{userEmail}</p>
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-500">Phone</p>
                  <p className="mt-0.5 font-semibold text-slate-900">{phoneDisplay}</p>
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-500">Total Spend</p>
                  <p className="mt-0.5 font-semibold text-slate-900">{money(totalSpendRaw, 'USD')}</p>
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-500">Joined Date</p>
                  <p className="mt-0.5 font-semibold text-slate-900">{userJoinedDateText}</p>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-5">
            <h3 className="text-sm font-black tracking-tight text-slate-900 flex items-center gap-2">
              <Truck className="w-4 h-4 text-primary" /> Shipping Address
            </h3>

            <div className="mt-4 space-y-4">
              {loadedOrder?.billingSameAsShipping ? (
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-black text-slate-700">Shipping & Billing address</p>
                    <span className="text-[11px] font-normal text-primary underline">Same Billing as Shipping</span>
                  </div>
                  <div className="mt-2 space-y-1 text-sm">
                    <p className="font-semibold text-slate-900">{loadedOrder?.shippingAddress?.fullName || '—'}</p>
                    <p className="text-slate-700">{loadedOrder?.shippingAddress?.phone || '—'}</p>
                    <p className="text-slate-700">
                      {[loadedOrder?.shippingAddress?.address, loadedOrder?.shippingAddress?.city, loadedOrder?.shippingAddress?.postalCode, loadedOrder?.shippingAddress?.country]
                        .filter(Boolean)
                        .join(', ') || '—'}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <p className="text-xs font-black text-slate-700">Shipping address</p>
                    <div className="mt-2 space-y-1 text-sm">
                      <p className="font-semibold text-slate-900">{loadedOrder?.shippingAddress?.fullName || '—'}</p>
                      <p className="text-slate-700">{loadedOrder?.shippingAddress?.phone || '—'}</p>
                      <p className="text-slate-700">
                        {[loadedOrder?.shippingAddress?.address, loadedOrder?.shippingAddress?.city, loadedOrder?.shippingAddress?.postalCode, loadedOrder?.shippingAddress?.country]
                          .filter(Boolean)
                          .join(', ') || '—'}
                      </p>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <p className="text-xs font-black text-slate-700">Billing address</p>
                    <div className="mt-2 space-y-1 text-sm">
                      <p className="font-semibold text-slate-900">{loadedOrder?.billingAddress?.fullName || '—'}</p>
                      <p className="text-slate-700">{loadedOrder?.billingAddress?.phone || '—'}</p>
                      <p className="text-slate-700">
                        {[loadedOrder?.billingAddress?.address, loadedOrder?.billingAddress?.city, loadedOrder?.billingAddress?.postalCode, loadedOrder?.billingAddress?.country]
                          .filter(Boolean)
                          .join(', ') || '—'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </Card>
        </div>
      </div>

      {invoiceModalOpen ? (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setInvoiceModalOpen(false)}
          />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Invoice PDF preview"
              className="flex h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-slate-500">Invoice PDF</p>
                  <h3 className="truncate text-sm font-black text-slate-900">Order #{headerId || 'Order'}</h3>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={downloadInvoice}
                    disabled={!pdfOrderId || invoiceDownloadStatus === 'loading'}
                    className={cx(
                      'inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-700 hover:bg-slate-50',
                      (!pdfOrderId || invoiceDownloadStatus === 'loading') ? 'opacity-60 cursor-not-allowed' : ''
                    )}
                  >
                    <Download className="h-4 w-4" />
                    {invoiceDownloadStatus === 'loading' ? 'Downloading...' : 'Download'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setInvoiceModalOpen(false)}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-700 hover:bg-slate-50"
                    aria-label="Close invoice PDF preview"
                  >
                    <X className="h-4 w-4" />
                    Close
                  </button>
                </div>
              </div>

              <div className="flex-1 bg-slate-100">
                {invoiceStatus === 'loading' ? (
                  <div className="flex h-full items-center justify-center text-sm font-semibold text-slate-600">
                    Loading invoice PDF...
                  </div>
                ) : invoiceStatus === 'failed' ? (
                  <div className="flex h-full items-center justify-center p-6">
                    <div className="max-w-md rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-800">
                      {String(invoiceError || 'Failed to load invoice PDF')}
                    </div>
                  </div>
                ) : invoicePreviewUrl ? (
                  <iframe
                    src={invoicePreviewUrl}
                    title={`Invoice PDF for order ${headerId || pdfOrderId}`}
                    className="h-full w-full border-0"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center p-6">
                    <button
                      type="button"
                      onClick={loadInvoicePreview}
                      className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-black text-white hover:bg-primary/90"
                    >
                      <FileText className="h-4 w-4" />
                      Load invoice PDF
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {cancelModalOpen ? (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => cancelStatus !== 'loading' && setCancelModalOpen(false)}
          />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Cancel order"
              className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl ring-1 ring-black/5"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-2">
                <span className="h-4 w-[3px] rounded-full bg-rose-500" aria-hidden="true" />
                <h3 className="text-sm font-black tracking-tight text-slate-900">
                  Cancel order #{headerId}
                </h3>
              </div>
              <p className="mt-2 text-xs text-slate-600">
                The order will be marked cancelled. This records the cancellation reason, who
                cancelled it, and when — it does not refund a paid order.
              </p>
              {isPaid ? (
                <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-900">
                  This order is PAID. After cancelling, issue the refund manually in the payment
                  provider — no refund is triggered automatically.
                </div>
              ) : null}
              <label className="mt-4 block text-xs font-bold text-slate-700">
                Cancellation reason (required)
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="e.g. Duplicate order, customer request, fraud check failed…"
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:ring-2 focus:ring-primary/10 outline-none"
                />
              </label>
              {cancelError ? (
                <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
                  {String(cancelError)}
                </div>
              ) : null}
              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setCancelModalOpen(false)}
                  disabled={cancelStatus === 'loading'}
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-700 hover:bg-slate-50"
                >
                  Keep order
                </button>
                <button
                  type="button"
                  onClick={confirmCancelOrder}
                  disabled={cancelStatus === 'loading' || !String(cancelReason).trim()}
                  className={cx(
                    'inline-flex items-center gap-2 rounded-xl bg-rose-600 px-4 py-2 text-xs font-black text-white hover:bg-rose-700',
                    (cancelStatus === 'loading' || !String(cancelReason).trim()) ? 'opacity-60 cursor-not-allowed' : ''
                  )}
                >
                  <XCircle className="w-4 h-4" />
                  {cancelStatus === 'loading' ? 'Cancelling…' : 'Confirm cancel'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
